import { useEffect, useState } from "react";
import { Loader2, RotateCcw, XCircle } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

/** A declined job is an empty car at pickup time — put it straight back in front of a chauffeur. */
export const ReofferCard = ({ trip, onDone }) => {
  const [chauffeurs, setChauffeurs] = useState([]);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get("/chauffeurs").then(({ data }) => setChauffeurs(data.filter((c) => c.email))).catch(() => {});
  }, []);

  const reoffer = async (chauffeurId) => {
    setBusy(chauffeurId || "same"); setError(""); setMsg("");
    try {
      const { data } = await api.post(`/manifests/${trip.id}/reoffer`,
        chauffeurId ? { chauffeur_id: chauffeurId } : {});
      setMsg(`Offered again to ${data.chauffeur_name || "the chauffeur"} — they have it in their app and by email.`);
      onDone?.();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  return (
    <div data-testid="reoffer-card" className="no-print rounded-xl border border-red-500/40 bg-red-500/5 p-5 sm:p-6">
      <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-red-300">
        <XCircle className="h-3.5 w-3.5" /> {trip.chauffeur_name || "The chauffeur"} declined this job
      </p>
      <p className="mt-2 text-sm text-slate-300">
        Nobody is driving trip #{trip.number} right now. Offer it again or hand it to someone else.
      </p>
      {error && <p data-testid="reoffer-error" className="mt-3 text-sm text-red-400">{error}</p>}
      {msg && <p data-testid="reoffer-msg" className="mt-3 text-sm text-emerald-400">{msg}</p>}

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
        {trip.chauffeur_email && (
          <Button data-testid="reoffer-same" disabled={busy !== ""} onClick={() => reoffer(null)}
            className="h-12 bg-gold text-ink-900 hover:bg-gold-light">
            {busy === "same" ? <Loader2 className="h-4 w-4 animate-spin" />
              : <><RotateCcw className="mr-2 h-4 w-4" /> Offer again to {trip.chauffeur_name || "them"}</>}
          </Button>
        )}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div>
            <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-slate-500">
              Or offer to someone else
            </label>
            <select data-testid="reoffer-select" value={pick} onChange={(e) => setPick(e.target.value)}
              className="h-12 w-full rounded-md border border-ink-600 bg-ink-900 px-3 text-sm text-slate-100 outline-none focus:ring-2 focus:ring-gold sm:w-56">
              <option value="">Choose a chauffeur</option>
              {chauffeurs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <Button data-testid="reoffer-other" variant="outline" disabled={busy !== "" || !pick}
            onClick={() => reoffer(pick)}
            className="h-12 border-ink-600 bg-transparent text-slate-300 hover:text-gold">
            {busy === pick ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send the job"}
          </Button>
        </div>
      </div>
    </div>
  );
};
